import React, { useMemo } from 'react';
import { METRIC_DEFS, EQUIPMENT_LABELS } from './equipmentDefs';

const TEAL = '#5fd4c4';
const BG = '#0a0f0a';
const MONO = "'JetBrains Mono', 'Courier New', monospace";

/**
 * MiniSparkline - SVG polyline of the last N values.
 */
function MiniSparkline({ values, color, width = 120, height = 28 }) {
    if (!values || values.length < 2) return null;

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const points = values.map((v, i) => {
        const x = (i / (values.length - 1)) * width;
        const y = height - ((v - min) / range) * (height - 4) - 2;
        return `${x},${y}`;
    }).join(' ');

    return (
        <svg width={width} height={height} style={{ display: 'block' }}>
            <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/**
 * EquipmentDetailPanel - HTML overlay showing live equipment metrics and sparkline.
 * Rendered as absolute-positioned overlay on top of the 3D viewport.
 *
 * @param {{
 *   equipment: object,
 *   equipmentType: string,
 *   history: Object<string, number[]>,
 *   onClose: function
 * }} props
 */
export default function EquipmentDetailPanel({ equipment, equipmentType, history, onClose }) {
    if (!equipment || !equipmentType) return null;

    const metrics = METRIC_DEFS[equipmentType] || [];
    const label = EQUIPMENT_LABELS[equipmentType] || equipmentType.toUpperCase();

    // Pick primary metric key for sparkline
    const primaryKey = metrics.length > 0 ? metrics[0].key : null;
    const sparklineData = primaryKey && history?.[primaryKey] ? history[primaryKey] : null;
    const sparklineColor = useMemo(() => {
        if (!metrics.length) return TEAL;
        const def = metrics[0];
        if (typeof def.color === 'function') {
            return def.color(equipment?.[def.key]);
        }
        return def.color;
    }, [metrics, equipment]);

    return (
        <div style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            width: 260,
            backgroundColor: BG,
            border: `1px solid ${TEAL}`,
            borderRadius: 4,
            padding: 14,
            fontFamily: MONO,
            color: '#f4f1ec',
            zIndex: 10,
            boxShadow: `0 0 20px ${TEAL}22, 0 4px 16px rgba(0,0,0,0.6)`,
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
                borderBottom: `1px solid ${TEAL}44`,
                paddingBottom: 8,
            }}>
                <span style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    color: TEAL,
                }}>
                    {label}
                </span>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: `1px solid ${TEAL}44`,
                        color: TEAL,
                        fontFamily: MONO,
                        fontSize: 10,
                        cursor: 'pointer',
                        padding: '2px 8px',
                        borderRadius: 2,
                    }}
                >
                    CLOSE
                </button>
            </div>

            {/* Metrics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {metrics.map((m) => {
                    const raw = equipment?.[m.key];
                    const display = m.format ? m.format(raw) : (raw != null ? Number(raw).toFixed(1) : '--');
                    const metricColor = typeof m.color === 'function' ? m.color(raw) : m.color;

                    return (
                        <div key={m.key} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                        }}>
                            <span style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {m.label}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: metricColor }}>
                                {display}
                                {m.unit && (
                                    <span style={{ fontSize: 9, fontWeight: 400, marginLeft: 3, color: '#666' }}>
                                        {m.unit}
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Sparkline */}
            {sparklineData && sparklineData.length > 1 && (
                <div style={{ marginTop: 10, borderTop: `1px solid ${TEAL}22`, paddingTop: 8 }}>
                    <div style={{ fontSize: 9, color: '#666', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {metrics[0]?.label || 'Value'} (recent)
                    </div>
                    <MiniSparkline values={sparklineData} color={sparklineColor} width={230} height={32} />
                </div>
            )}
        </div>
    );
}
